/** API service for user preferences. */

import axios, { AxiosResponse } from 'axios';
import { auth } from '../lib/firebase';
import type { DevelopmentMandateSelections } from '@/config/developmentMandate';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const preferencesApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

preferencesApi.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-User-Email'] = user.email || '';
  }
  return config;
});

export type UserType = 'creator' | 'decision_maker';

export interface UserPreferences {
  user_id?: string;
  user_type?: UserType;
  development_mandate?: DevelopmentMandateSelections | null;
  is_admin?: boolean;
  is_reviewer?: boolean;
  updated_at?: string | null;
}

export interface PreferencesResponse {
  status: string;
  preferences: UserPreferences;
  reassessed_scripts?: number;
  error?: string;
}

export type PreferencesUpdateResult = {
  preferences: UserPreferences;
  reassessed_scripts: number;
};

export async function getPreferences(): Promise<UserPreferences> {
  try {
    const response: AxiosResponse<PreferencesResponse> = await preferencesApi.get(
      '/api/user/preferences'
    );

    if (response.data.status !== 'success') {
      throw new Error(response.data.error || 'Failed to get preferences');
    }

    return response.data.preferences;
  } catch (error: any) {
    console.error('Error getting preferences:', error);
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to get preferences');
  }
}

export async function updatePreferences(
  preferences: Partial<UserPreferences>,
): Promise<PreferencesUpdateResult> {
  try {
    const response: AxiosResponse<PreferencesResponse> = await preferencesApi.put(
      '/api/user/preferences',
      preferences,
    );

    if (response.data.status !== 'success') {
      throw new Error(response.data.error || 'Failed to update preferences');
    }

    return {
      preferences: response.data.preferences,
      reassessed_scripts: response.data.reassessed_scripts ?? 0,
    };
  } catch (error: any) {
    console.error('Error updating preferences:', error);
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to update preferences');
  }
}
